import { first } from "lodash";
import { FC, ReactNode, useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AnySchema, mixed, object, string } from "yup";
import DriverIllustration from "../../../../../assets/driver-ok.jpg";
import IdentityIllustration from "../../../../../assets/id-ok.jpg";
import PassportIllustration from "../../../../../assets/passport-ok.jpg";
import { FileField } from "../../../../../components/forms/file-3";
import { RadioGroup } from "../../../../../components/forms/radio";
import { GridItem } from "../../../../../components/layout";
import {
  IdentityAttachmentType,
  useIdentityAttachmentType,
} from "../../../../common/models";
import { Field } from "../../internal/field";
import { ColumnWrapper, RenderFuncProps } from "../common";

export const List = styled.ul`
  margin: 0;
  padding-left: 2em;
`;

export const Instructions = styled.div`
  margin: 1em 0;
`;

export const methodsWithTwoSides: IdentityAttachmentType[] = [
  "DRIVING_LICENSE",
  "ID_CARD",
];

export const IllustrationMap: Record<IdentityAttachmentType, string> = {
  DRIVING_LICENSE: DriverIllustration,
  ID_CARD: IdentityIllustration,
  PASSPORT: PassportIllustration,
};

const Elem: FC<
  RenderFuncProps & { path: string; additionalField: ReactNode }
> = ({ path, item, additionalField }) => {
  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);
  const options = useMemo(
    () =>
      Field.isIdentityDocumentField(item) &&
      item.typeDetails.availableOptions?.[currentLanguage]
        ? (item.typeDetails.availableOptions[
            currentLanguage
          ] as IdentityAttachmentType[])
        : [],
    [item, currentLanguage]
  );

  const defaultAttachmentType = useMemo(
    () =>
      Field.isIdentityDocumentField(item) &&
      item.typeDetails.availableOptions?.[currentLanguage]
        ? first(
            item.typeDetails.availableOptions[
              currentLanguage
            ] as IdentityAttachmentType[]
          )
        : undefined,
    [item, currentLanguage]
  );

  const { control } = useFormContext();
  const typeField = useController({
    control,
    name: `${path}.type`,
    defaultValue: defaultAttachmentType,
  });

  const fileField = useController({
    control,
    name: `${path}.file`,
  });

  const hasTwoSides = useMemo(
    () => methodsWithTwoSides.includes(typeField.field.value),
    [typeField.field.value]
  );

  const identityAttachmentType = useIdentityAttachmentType();

  return (
    <>
      <GridItem top xs={12}>
        {t("choose-your-document-type")}
      </GridItem>
      <GridItem top xs={12} md={6} column>
        <RadioGroup<IdentityAttachmentType>
          fullWidth
          options={options.map((v) => ({
            label:
              identityAttachmentType.enum[v as IdentityAttachmentType] || v,
            id: v,
          }))}
          onChange={(x) => typeField.field.onChange(x)}
          value={typeField.field.value}
        />
        <Instructions>
          {t("take-a-photo-of-your", {
            type:
              identityAttachmentType.enum[
                typeField.field.value as IdentityAttachmentType
              ],
          })}
        </Instructions>
        <List dangerouslySetInnerHTML={{ __html: t("photo-qualities") }} />
      </GridItem>
      <GridItem top xs={12} md={6}>
        <img
          src={IllustrationMap[typeField.field.value as IdentityAttachmentType]}
        />
      </GridItem>
      <GridItem top xs={12}>
        {t("upload-your-document")}
      </GridItem>
      <GridItem top xs={12} sm={12} md={hasTwoSides ? 6 : 12}>
        <ColumnWrapper>
          <FileField
            onChange={(x) => fileField.field.onChange(x)}
            value={fileField.field.value}
            error={!!fileField.fieldState.error}
            label={t(hasTwoSides ? "front-side" : "identity-document")}
            helperText={fileField.fieldState.error?.message}
          />
        </ColumnWrapper>
      </GridItem>
      {hasTwoSides ? (
        <GridItem top item xs={12} sm={12} md={6}>
          {additionalField}
        </GridItem>
      ) : null}
    </>
  );
};

const useSchema = () => {
  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);
  return useCallback<(f: Field.DefaultField) => AnySchema>(
    (f: Field.DefaultField) =>
      object()
        .shape({
          type: Field.isIdentityDocumentField(f)
            ? mixed()
                .oneOf(f.typeDetails.availableOptions?.[currentLanguage] || [])
                .label(t("identity-document-type"))
                .required(
                  t("required-field", {
                    ns: "errors",
                    field: t("identity-document-type"),
                  })
                )
            : string(),
          file: mixed()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("identity-document-file"),
              })
            )
            .test(
              "is-large",
              t(`file-size-cannot-exceed-6-mb`),
              (file: any) =>
                !(file instanceof File) || file.size <= 6 * 1024 * 1024
            )
            .label(t("identity-document-file")),
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("identity-document"),
          })
        )
        .label(t("identity-document")),
    [t, currentLanguage]
  );
};

export const useIdentityDocumentAttachment = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
    }),
    [schema]
  );
};
